import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Phone,
  Mail,
  MapPin,
  DollarSign,
  Users,
  CreditCard,
  Loader,
  Percent,
  FileText,
  X
} from 'lucide-react';
import { toast } from 'react-toastify';
import { formatDateToISO } from '../utils/formatters';
import { useAuth } from '../contexts/AuthContext';
import {
  getReferrerById,
  deleteReferrer,
  getReferrerCustomers,
  generateReferrerStatement,
  getReferrerCommissionTrend
} from '../api/referrers';
import CommissionAnalyticsChart from '../components/CommissionAnalyticsChart';
import LoadingSpinner from '../components/common/LoadingSpinner';

const ReferrerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [referrer, setReferrer] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [statementDates, setStatementDates] = useState({
    startDate: formatDateToISO(new Date(new Date().setDate(new Date().getDate() - 30))),
    endDate: formatDateToISO(new Date())
  });
  const [generatingStatement, setGeneratingStatement] = useState(false);
  const [commissionTrend, setCommissionTrend] = useState([]);

  const fetchReferrer = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getReferrerById(id);
      setReferrer(data);
    } catch (error) {
      toast.error('Failed to fetch referrer details');
      navigate('/referrers');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchReferrerCustomers = useCallback(async () => {
    setLoadingCustomers(true);
    try {
      const data = await getReferrerCustomers(id);
      setCustomers(data.customers || []);
    } catch (error) {
    } finally {
      setLoadingCustomers(false);
    }
  }, [id]);

  const fetchCommissionTrend = useCallback(async () => {
    try {
      const data = await getReferrerCommissionTrend(id);
      setCommissionTrend(data);
    } catch (error) {}
  }, [id]);

  useEffect(() => {
    fetchReferrer();
    fetchReferrerCustomers();
    fetchCommissionTrend();
  }, [fetchReferrer, fetchReferrerCustomers, fetchCommissionTrend]);

  const handleDelete = async () => {
    if (
      !window.confirm(
        'Are you sure you want to delete this referrer? This action cannot be undone.'
      )
    ) {
      return;
    }

    try {
      await deleteReferrer(id);
      toast.success('Referrer deleted successfully');
      navigate('/referrers');
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to delete referrer';
      toast.error(errorMessage);
    }
  };

  const handleGenerateStatement = async () => {
    setGeneratingStatement(true);
    try {
      await generateReferrerStatement(id, statementDates.startDate, statementDates.endDate);
      toast.success('Statement generated successfully');
      setShowStatementModal(false);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to generate statement';
      toast.error(errorMessage);
    } finally {
      setGeneratingStatement(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const isOwnerBranch = referrer?.created_by_branch_id === user?.current_branch_id;
  const canEdit =
    ['super_admin', 'admin', 'retail_admin'].includes(user?.role) ||
    (user?.role === 'branch_admin' && isOwnerBranch);

  if (loading) {
    return <LoadingSpinner className="min-h-screen" />;
  }

  if (!referrer) {
    return (
      <div className="min-h-screen bg-secondary-offWhite flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-700 mb-2">Referrer Not Found</h2>
          <button
            onClick={() => navigate('/referrers')}
            className="text-accent-gold hover:underline"
          >
            Back to Referrers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-offWhite">
      <div className="max-w-6xl mx-auto p-4 md:p-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/referrers')}
            className="flex items-center gap-2 text-gray-600 hover:text-primary-black mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Referrers
          </button>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-primary-black">
                {referrer.full_name}
              </h1>
              <p className="text-gray-600 mt-1">{referrer.referrer_code}</p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setShowStatementModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-accent-gold text-white rounded-lg hover:bg-accent-gold/90 transition-colors"
              >
                <FileText className="w-5 h-5" />
                Generate Statement
              </button>
              {canEdit && (
                <>
                  <button
                    onClick={() => navigate(`/referrers/${id}/edit`)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Edit className="w-5 h-5" />
                    Edit
                  </button>
                  <button
                    onClick={handleDelete}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-primary-black mb-4">Basic Information</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-500">Full Name</label>
                  <p className="font-semibold text-gray-900">{referrer.full_name}</p>
                </div>

                <div>
                  <label className="text-sm text-gray-500">Referrer Code</label>
                  <p className="font-semibold text-gray-900">{referrer.referrer_code}</p>
                </div>

                <div>
                  <label className="text-sm text-gray-500">Status</label>
                  <span
                    className={`inline-block px-3 py-1 text-sm font-semibold rounded ${
                      referrer.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {referrer.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div>
                  <label className="text-sm text-gray-500">Commission Type</label>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900">
                      {referrer.commission_type === 'percentage' ? (
                        <span className="flex items-center gap-1">
                          <Percent className="w-4 h-4" />
                          {referrer.commission_rate}%
                        </span>
                      ) : (
                        formatCurrency(referrer.fixed_commission_amount)
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-primary-black mb-4">Contact Information</h2>

              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-gray-500" />
                  <div>
                    <label className="text-sm text-gray-500">Phone</label>
                    <p className="font-semibold text-gray-900">{referrer.phone}</p>
                  </div>
                </div>

                {referrer.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-gray-500" />
                    <div>
                      <label className="text-sm text-gray-500">Email</label>
                      <p className="font-semibold text-gray-900">{referrer.email}</p>
                    </div>
                  </div>
                )}

                {referrer.address && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-gray-500 mt-1" />
                    <div>
                      <label className="text-sm text-gray-500">Address</label>
                      <p className="font-semibold text-gray-900">{referrer.address}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Bank Information */}
            {(referrer.bank_name || referrer.account_number) && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-primary-black mb-4 flex items-center gap-2">
                  <CreditCard className="w-6 h-6" />
                  Bank Information
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {referrer.bank_name && (
                    <div>
                      <label className="text-sm text-gray-500">Bank Name</label>
                      <p className="font-semibold text-gray-900">{referrer.bank_name}</p>
                    </div>
                  )}

                  {referrer.account_number && (
                    <div>
                      <label className="text-sm text-gray-500">Account Number</label>
                      <p className="font-semibold text-gray-900">{referrer.account_number}</p>
                    </div>
                  )}

                  {referrer.account_name && (
                    <div className="md:col-span-2">
                      <label className="text-sm text-gray-500">Account Name</label>
                      <p className="font-semibold text-gray-900">{referrer.account_name}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Referred Customers */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-primary-black mb-4 flex items-center gap-2">
                <Users className="w-6 h-6" />
                Referred Customers ({customers.length})
              </h2>

              {loadingCustomers ? (
                <div className="flex items-center justify-center py-8">
                  <Loader className="w-8 h-8 text-accent-gold animate-spin" />
                </div>
              ) : customers.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No customers referred yet</p>
              ) : (
                <div className="space-y-3">
                  {customers.map((customer) => (
                    <div
                      key={customer.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                      onClick={() => navigate(`/customers/${customer.id}`)}
                    >
                      <div>
                        <p className="font-semibold text-gray-900">{customer.full_name}</p>
                        <p className="text-sm text-gray-600">{customer.customer_code}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500">Total Purchases</p>
                        <p className="font-bold text-accent-gold">
                          {formatCurrency(customer.total_purchases)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Commission Trend Chart */}
            <CommissionAnalyticsChart data={commissionTrend} chartType="area" />
          </div>

          {/* Commission Summary */}
          <div className="space-y-6">
            {/* Commission Stats */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-primary-black mb-4 flex items-center gap-2">
                <DollarSign className="w-6 h-6" />
                Commission Summary
              </h2>

              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-500">
                  <label className="text-sm text-blue-700">Total Referrals</label>
                  <p className="text-2xl font-bold text-blue-900">
                    {referrer.total_referrals || 0}
                  </p>
                </div>

                <div className="p-4 bg-purple-50 rounded-lg border-2 border-purple-500">
                  <label className="text-sm text-purple-700">Total Sales Value</label>
                  <p className="text-2xl font-bold text-purple-900">
                    {formatCurrency(referrer.total_sales_value)}
                  </p>
                </div>

                <div className="p-4 bg-green-50 rounded-lg border-2 border-green-500">
                  <label className="text-sm text-green-700">Total Earned</label>
                  <p className="text-2xl font-bold text-green-900">
                    {formatCurrency(referrer.total_commission_earned)}
                  </p>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg border-2 border-gray-500">
                  <label className="text-sm text-gray-700">Total Paid</label>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(referrer.total_commission_paid)}
                  </p>
                </div>

                <div className="p-4 bg-yellow-50 rounded-lg border-2 border-yellow-500">
                  <label className="text-sm text-yellow-700">Unpaid Balance</label>
                  <p className="text-2xl font-bold text-yellow-900">
                    {formatCurrency(referrer.commission_balance)}
                  </p>
                </div>
              </div>
            </div>

            {/* Meta Information */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-primary-black mb-4">Additional Info</h2>

              <div className="space-y-3">
                <div>
                  <label className="text-sm text-gray-500">Created Date</label>
                  <p className="font-semibold text-gray-900">{formatDate(referrer.created_at)}</p>
                </div>

                {referrer.updated_at && (
                  <div>
                    <label className="text-sm text-gray-500">Last Updated</label>
                    <p className="font-semibold text-gray-900">{formatDate(referrer.updated_at)}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Statement Generation Modal */}
        {showStatementModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-primary-black">
                  Generate Commission Statement
                </h2>
                <button
                  onClick={() => setShowStatementModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={statementDates.startDate}
                    onChange={(e) =>
                      setStatementDates({ ...statementDates, startDate: e.target.value })
                    }
                    max={statementDates.endDate}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                  <input
                    type="date"
                    value={statementDates.endDate}
                    onChange={(e) =>
                      setStatementDates({ ...statementDates, endDate: e.target.value })
                    }
                    min={statementDates.startDate}
                    max={formatDateToISO(new Date())}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowStatementModal(false)}
                  disabled={generatingStatement}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleGenerateStatement}
                  disabled={generatingStatement}
                  className="flex-1 px-4 py-2 bg-accent-gold text-white rounded-lg hover:bg-accent-gold/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {generatingStatement ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <FileText className="w-5 h-5" />
                      Generate PDF
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferrerDetailPage;
